export type GeoFaqItem = { question: string; answer: string };

export type GeoFeaturePageData = {
  path: string;
  title: string;
  description: string;
  breadcrumbName: string;
  h1: string;
  intro: string;
  sections: Array<{ heading: string; paragraphs: string[]; bullets?: string[] }>;
  related: Array<{ to: string; label: string }>;
  faq: GeoFaqItem[];
};

export const GEO_FEATURE_PAGES: GeoFeaturePageData[] = [
  {
    path: "/whatsapp/disparos",
    title: "Disparo de mensagens no WhatsApp | FabChat",
    description:
      "Disparo em massa no WhatsApp pela FabChat: API oficial da Meta (Cloud) e conexão por QR. Segmente leads e envie campanhas no painel.",
    breadcrumbName: "Disparo de mensagens no WhatsApp",
    h1: "Disparo de mensagens no WhatsApp",
    intro:
      "A FabChat permite enviar mensagens em massa para leads selecionados no WhatsApp, pela API oficial da Meta (Cloud API, com templates) ou pela conexão rápida via QR Code. O disparo é um recurso da operação comercial, separado do atendimento conversacional do agente de IA.",
    sections: [
      {
        heading: "Como funciona o disparo na FabChat",
        paragraphs: [
          "No painel, você escolhe leads, monta a campanha e acompanha envios. Antes de usar o recurso, a plataforma pede ciência dos riscos de envio em massa, especialmente fora da API oficial.",
          "O disparo não substitui o agente de IA: o agente responde conversas em andamento; o disparo inicia ou retoma contato em escala, com os limites do canal escolhido.",
        ],
      },
      {
        heading: "API oficial e conexão por QR",
        paragraphs: [
          "Há dois caminhos de WhatsApp na FabChat, e o disparo respeita o canal conectado na conta:",
        ],
        bullets: [
          "API oficial (WhatsApp Cloud API / Meta): conexão pela conta Business da Meta, alinhada às políticas da WhatsApp Business Platform. Envios de marketing usam templates aprovados quando a regra da Meta exigir.",
          "Conexão por QR Code (não oficial): você vincula o WhatsApp Business escaneando um QR, como em um segundo aparelho. É mais rápida de ligar, mas não é a API oficial da Meta e está sujeita a bloqueios da conta se houver uso indevido ou volume incompatível com as regras do WhatsApp.",
        ],
      },
      {
        heading: "Para quem é indicado",
        paragraphs: [
          "Times que já organizam contatos no CRM da FabChat e precisam avisar base, reativar leads ou comunicar oferta, cientes das políticas do WhatsApp e da diferença entre API oficial e QR.",
        ],
      },
    ],
    related: [
      { to: "/whatsapp/agente-ia", label: "Agente de IA no WhatsApp" },
      { to: "/integracoes/crm", label: "CRM integrado" },
      { to: "/recuperacao-de-leads", label: "Recuperação de leads" },
    ],
    faq: [
      {
        question: "A FabChat dispara mensagens pela API oficial do WhatsApp?",
        answer:
          "Sim, quando o número está conectado pela WhatsApp Cloud API da Meta. Nesse modo, campanhas seguem as regras da plataforma oficial, inclusive templates aprovados quando aplicável.",
      },
      {
        question: "Posso disparar pela conexão de QR Code?",
        answer:
          "Sim, se o workspace estiver com WhatsApp ligado via QR. Essa conexão não é a API oficial da Meta. Envios em massa por esse caminho aumentam o risco de restrição da conta; a FabChat exige aceite explícito desses riscos.",
      },
      {
        question: "Disparo e agente de IA são a mesma coisa?",
        answer:
          "Não. O agente de IA responde conversas com contexto e regras. O disparo envia mensagens em lote para leads escolhidos. Os dois convivem no mesmo painel e CRM.",
      },
    ],
  },
  {
    path: "/recuperacao-de-leads",
    title: "Recuperação de leads com IA | FabChat",
    description:
      "Recuperação de lead na FabChat: a IA sugere respostas em tons suave, médio e assertivo a partir do histórico da conversa.",
    breadcrumbName: "Recuperação de leads",
    h1: "Recuperação de leads com sugestão de respostas",
    intro:
      "Quando um lead esfria no WhatsApp ou no Instagram, a FabChat analisa o histórico da conversa e sugere mensagens para reabrir o diálogo. Você escolhe o tom, edita o texto e envia pelo chat — não é um disparo automático cego.",
    sections: [
      {
        heading: "O que a recuperação de lead faz",
        paragraphs: [
          "A função lê o contexto da conversa, resume o que aconteceu, aponta objeções percebidas e recomenda uma abordagem. Em seguida oferece três textos: suave, médio e mais assertivo.",
          "Você pode copiar a sugestão ou inserir no campo de mensagem do atendimento. A decisão de enviar continua humana.",
        ],
        bullets: [
          "Resumo do contexto e das objeções da conversa",
          "Três tons de mensagem para reativar o contato",
          "Uso no inbox, ligado ao lead e ao histórico no CRM",
        ],
      },
      {
        heading: "Diferença para disparo e para o agente",
        paragraphs: [
          "O agente de IA atende sozinho quando está ativo. O disparo fala com muitos leads de uma vez. A recuperação de lead ajuda o atendente a escrever a próxima mensagem certa para um contato específico que parou de responder.",
        ],
      },
    ],
    related: [
      { to: "/qualificacao-de-leads-no-whatsapp", label: "Qualificação de leads" },
      { to: "/whatsapp/disparos", label: "Disparos no WhatsApp" },
      { to: "/aprimorar-mensagens", label: "Aprimorar mensagens" },
    ],
    faq: [
      {
        question: "A recuperação de lead envia a mensagem sozinha?",
        answer:
          "Não. A IA sugere textos com base no histórico. Quem envia é a equipe, pelo chat da conversa.",
      },
      {
        question: "Funciona no WhatsApp e no Instagram?",
        answer:
          "Sim. A sugestão parte da conversa aberta no painel, seja o canal WhatsApp ou Instagram, com o lead no CRM.",
      },
    ],
  },
  {
    path: "/agendar-mensagens",
    title: "Agendar mensagens no WhatsApp | FabChat",
    description:
      "Agende mensagens no WhatsApp e Instagram pela FabChat: programe envios e mensagens de encerramento para o horário certo.",
    breadcrumbName: "Agendar mensagens",
    h1: "Agendar mensagens",
    intro:
      "Além do agente responder na hora, a FabChat permite programar envios para um contato: follow-up, recado combinado ou mensagem de encerramento no horário definido pela equipe.",
    sections: [
      {
        heading: "O que você programa",
        paragraphs: [
          "No atendimento, a equipe agenda o envio para o cliente certo, na data e hora escolhidas. Serve para não esquecer um retorno e para encerrar conversas com um recado padronizado no momento combinado.",
        ],
        bullets: [
          "Envios programados na conversa",
          "Mensagens de encerramento agendadas",
          "Uso junto ao agente, ao CRM e às tarefas da equipe",
        ],
      },
      {
        heading: "Não é disparo em massa",
        paragraphs: [
          "Agendar mensagem é pontual, por conversa. Campanha para muitos leads é o disparo. Compromissos de visita ou consulta ficam em Agendamentos, com Google Calendar quando a integração estiver ligada.",
        ],
      },
    ],
    related: [
      { to: "/whatsapp/disparos", label: "Disparos" },
      { to: "/tarefas", label: "Tarefas" },
      { to: "/whatsapp/agente-ia", label: "Agente de IA" },
    ],
    faq: [
      {
        question: "Posso agendar uma mensagem para um cliente no WhatsApp?",
        answer:
          "Sim. Na conversa você programa o envio para data e hora futuras, inclusive recados de encerramento.",
      },
    ],
  },
  {
    path: "/catalogo-de-produtos",
    title: "Catálogo de produtos com IA | FabChat",
    description:
      "Cadastre produtos, serviços e planos na FabChat e deixe o agente de IA responder preço e disponibilidade.",
    breadcrumbName: "Catálogo de produtos",
    h1: "Catálogo de produtos",
    intro:
      "A FabChat tem catálogo nativo: produtos físicos, serviços e planos. O agente de IA consulta esse cadastro nas conversas de WhatsApp e Instagram, em vez de inventar preço ou estoque.",
    sections: [
      {
        heading: "Como o catálogo entra no atendimento",
        paragraphs: [
          "Você cadastra itens no painel ou importa em massa. Depois associa produtos ao agente. Na conversa, a busca usa o cadastro real do workspace.",
        ],
        bullets: [
          "Cadastro de produto, serviço ou plano",
          "Importação em massa e organização no painel",
          "Respostas do agente ancoradas no catálogo, não em chute",
        ],
      },
    ],
    related: [
      { to: "/whatsapp/agente-ia", label: "Agente de IA no WhatsApp" },
      { to: "/integracoes/crm", label: "CRM" },
      { to: "/negociacoes", label: "Negociações" },
    ],
    faq: [
      {
        question: "O agente responde com os produtos que eu cadastrei?",
        answer:
          "Sim. Preço, variação e disponibilidade comerciais vêm do catálogo do workspace e dos produtos associados ao agente.",
      },
    ],
  },
  {
    path: "/negociacoes",
    title: "Negociações e pipeline comercial | FabChat",
    description:
      "Negociações na FabChat: pipeline comercial ligado aos leads do CRM, com valores e etapas separados do funil de atendimento.",
    breadcrumbName: "Negociações",
    h1: "Negociações",
    intro:
      "Além do funil de leads, a FabChat tem pipeline de negociações: oportunidades com valor e etapa comercial, ligadas ao contato que veio do WhatsApp ou do Instagram.",
    sections: [
      {
        heading: "Leads e negociações",
        paragraphs: [
          "O CRM organiza o estágio do relacionamento (novo, qualificado, em atendimento). A negociação registra a oportunidade de venda: valor, etapa do pipeline e desfecho.",
          "Você cria a negociação a partir do lead e acompanha o pipeline sem misturar com o kanban de atendimento.",
        ],
      },
    ],
    related: [
      { to: "/integracoes/crm", label: "CRM de leads" },
      { to: "/qualificacao-de-leads-no-whatsapp", label: "Qualificação de leads" },
      { to: "/tarefas", label: "Tarefas" },
    ],
    faq: [
      {
        question: "Negociação é o mesmo que o funil de leads?",
        answer:
          "Não. O funil de leads acompanha o contato no atendimento. A negociação é o pipeline comercial da oportunidade, com valor e etapas próprias.",
      },
    ],
  },
  {
    path: "/tarefas",
    title: "Tarefas da equipe | FabChat",
    description:
      "Tarefas na FabChat: pendências, urgências e acompanhamento do dia para o time que atende WhatsApp e Instagram.",
    breadcrumbName: "Tarefas",
    h1: "Tarefas da equipe",
    intro:
      "A FabChat distribui tarefas no mesmo workspace do chat: follow-up, pendência interna e o que a equipe precisa concluir no dia, com visibilidade de progresso.",
    sections: [
      {
        heading: "Uso no dia a dia",
        paragraphs: [
          "Enquanto o agente e o CRM cuidam da conversa e do lead, as tarefas organizam o trabalho humano: quem faz o quê, o que está atrasado e o que é urgente.",
        ],
        bullets: [
          "Pendências ligadas à operação de atendimento",
          "Acompanhamento do progresso da equipe",
          "Complemento a agendar mensagem e à recuperação de lead",
        ],
      },
    ],
    related: [
      { to: "/agendar-mensagens", label: "Agendar mensagens" },
      { to: "/analise-de-atendimento", label: "Análise do atendimento" },
      { to: "/integracoes/crm", label: "CRM" },
    ],
    faq: [
      {
        question: "As tarefas ficam no mesmo painel das conversas?",
        answer:
          "Sim. Tarefas, conversas, CRM e agentes compartilham o workspace da FabChat.",
      },
    ],
  },
  {
    path: "/aprimorar-mensagens",
    title: "Aprimorar mensagens com IA | FabChat",
    description:
      "Melhore o texto antes de enviar no WhatsApp ou Instagram: a FabChat reescreve a mensagem com IA, no tom que você escolher.",
    breadcrumbName: "Aprimorar mensagens",
    h1: "Aprimorar mensagens com IA",
    intro:
      "No chat, a equipe pode pedir à IA para deixar o texto mais claro ou profissional antes do envio. Diferente do agente automático, aqui quem dispara a melhoria é o atendente, sobre a mensagem que está escrevendo.",
    sections: [
      {
        heading: "Quando usar",
        paragraphs: [
          "Útil em respostas delicadas, follow-up e recuperação de lead: você mantém a intenção e a IA ajusta clareza e tom. A mensagem só sai depois da sua confirmação.",
        ],
      },
    ],
    related: [
      { to: "/recuperacao-de-leads", label: "Recuperação de leads" },
      { to: "/whatsapp/agente-ia", label: "Agente de IA" },
    ],
    faq: [
      {
        question: "Aprimorar mensagem substitui o agente de IA?",
        answer:
          "Não. O agente atende sozinho segundo as regras. Aprimorar mensagem ajuda o humano a reescrever um texto pontual antes de enviar.",
      },
    ],
  },
  {
    path: "/analise-de-atendimento",
    title: "Análise de atendimento | FabChat",
    description:
      "Dashboard e resumo de conversas na FabChat: métricas da equipe, horários de pico e síntese do atendimento por IA.",
    breadcrumbName: "Análise de atendimento",
    h1: "Análise do atendimento",
    intro:
      "A FabChat reúne métricas no dashboard e gera resumo da conversa com IA, para passar o caso, encerrar o atendimento ou alinhar o time sem reler o histórico inteiro.",
    sections: [
      {
        heading: "O que a análise cobre",
        paragraphs: [
          "No dashboard você acompanha volume de conversas, leads, desempenho da equipe e horários de pico. No chat, o resumo condensa o atendimento em poucos segundos.",
        ],
        bullets: [
          "Indicadores operacionais no dashboard",
          "Resumo da conversa gerado por IA",
          "Visão junto ao CRM, tarefas e agentes",
        ],
      },
    ],
    related: [
      { to: "/integracoes/crm", label: "CRM" },
      { to: "/tarefas", label: "Tarefas" },
      { to: "/recuperacao-de-leads", label: "Recuperação de leads" },
    ],
    faq: [
      {
        question: "A FabChat resume conversas automaticamente?",
        answer:
          "A equipe pode gerar um resumo da conversa no painel. O dashboard mostra métricas contínuas de atendimento e operação.",
      },
    ],
  },
];
